/**
 * Format a US phone number for display.
 *
 * Strips non-digit characters first so input can be raw, hyphenated, or
 * already partially formatted. Then groups based on digit count:
 *   - 10 digits        → (XXX) XXX-XXXX
 *   - 11 with leading 1 → (XXX) XXX-XXXX  (drops the country-code 1)
 *   - 7 digits         → XXX-XXXX
 *   - 8 or 9 digits    → (XXX) XXX-X[XX]   (best-effort, last group short)
 *   - anything else    → original input unchanged
 *
 * Best-effort formatting on short inputs lets the UI surface "this looks
 * truncated" visually rather than dumping unformatted digits. Users can
 * fix a malformed number from their profile editor.
 */
export function formatPhoneNumber(phone) {
  if (!phone) return ''
  let digits = String(phone).replace(/\D/g, '')

  // Strip leading 1 country code so 11-digit US numbers format like 10-digit.
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1)
  }

  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  if (digits.length === 7) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`
  }
  if (digits.length === 8 || digits.length === 9) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return phone
}

/**
 * Format a partially-typed phone number for display in an input field.
 * Lets the user see the shape "(402) 555-12" emerge as they type, while
 * the form state itself stores raw digits only.
 *
 * Differs from formatPhoneNumber in that intermediate digit counts (1, 4,
 * 7) get formatted progressively rather than passed through unchanged.
 */
export function formatPhoneTyping(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 10)
  if (digits.length === 0) return ''
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

export default formatPhoneNumber
